import React from "react";
import { FormControl, FormHelperText, TextField } from "@mui/material";
import { Field } from "formik";
import PropTypes from "prop-types";
import FormikLabel from "./FormLabel";

const TextFieldComponent = ({
  field,
  errors,
  touched,
  handleFieldChange,
  values,
}) => {
  // Define input type based on validation
  const inputType =
    field.validation?.inputType === "number"
      ? "number"
      : field.validation?.inputType === "email"
      ? "email"
      : "text";

  // Email validation regex pattern
  const emailPattern = "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$";

  // Conditionally set inputProps
  const inputProps = {
    ...(inputType === "email" ? { pattern: emailPattern } : {}),
    ...(inputType === "number"
      ? {
          inputMode: "numeric",
          pattern: "[0-9]*",
          onKeyDown: (event) => {
            if (["e", "E", "+", "-", "."].includes(event.key)) {
              event.preventDefault(); 
            }
          },
        }
      : {}),
  };

  const isError = touched[field.fieldId] && !!errors[field.fieldId];

  return (
    <FormControl fullWidth error={isError}>
      <FormikLabel text={field.fieldName} required={field.required} />
      <Field name={field.fieldId}>
        {({ field: formikField }) => (
          <TextField
            {...formikField}
            type={inputType} // Use dynamically assigned input type
            placeholder={field.placeholder || field.fieldName}
            disabled={field.disabled}
            variant="outlined"
            value={values?.[field.fieldId] || formikField.value || ""}
            inputProps={inputProps}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                borderWidth: "1px",
                cursor: field.disabled ? "not-allowed" : "inherit",
                borderColor: isError ? "red" : "blue",
              },
              "& .MuiInputBase-input": {
                padding: "8px",
                borderRadius: "8px",
                color: field.disabled ? "#a0a0a0" : "inherit",
                cursor:
                  field.disabled || field.readOnly ? "not-allowed" : "inherit",
                backgroundColor:
                  field.disabled || field.readOnly ? "#f5f5f5" : "inherit",
              },
            }}
            onChange={(event) => {
              formikField.onChange(event); // Call Formik's onChange
              handleFieldChange(event, field.fieldId); // Call custom change handler
            }}
          />
        )}
      </Field>
      <FormHelperText>{isError && errors[field.fieldId]}</FormHelperText>
    </FormControl>
  );
};

TextFieldComponent.propTypes = {
  field: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
};

export default TextFieldComponent;
